<template>
  <div class="d-flex gap-2" :class="column ? 'flex-column' : 'align-center'">
    <v-switch
      :model-value="modelValue.inHouse"
      hide-details
      class="mt-0"
      label="In House Only"
      :loading="filterLoading"
      @update:model-value="updateFilters('inHouse', Boolean($event))"
    />
    <b v-if="dateLabel" class="text-medium-emphasis">{{ dateLabel }}</b>
    <div v-if="defaultStartDate">{{ $filters.formatDate(defaultStartDate) }} - {{ $filters.formatDate(endDate) }}</div>
    <div v-else class="d-flex align-center gap-2">
      <v-menu
        v-model="dateStartMenu"
        :close-on-content-click="false"
        location="top end"
        origin="overlap"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="outlined"
          >
            <v-icon size="small">mdi-calendar</v-icon> From: {{ $filters.formatDate(modelValue.dateStart) }}
          </v-btn>
        </template>
        <v-date-picker v-model="dateStartModel" />
      </v-menu>
      <v-menu
        v-model="dateEndMenu"
        :close-on-content-click="false"
        location="top end"
        origin="overlap"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="outlined"
          >
            <v-icon size="small">mdi-calendar</v-icon> To: {{ $filters.formatDate(endDate) }}
          </v-btn>
        </template>
        <v-date-picker v-model="dateEndModel" />
      </v-menu>
    </div>
    <v-select
      :model-value="modelValue.propertyType"
      label="Select Property Type"
      hide-details
      :items="propertyTypes"
      :loading="loading"
      :style="selectStyle"
      clearable
      @update:model-value="updateFilters('propertyType', $event || null)"
    />
    <SavedSetSelect
      :model-value="modelValue.savedSet"
      :source="source"
      :style="selectStyle"
      @update:model-value="updateFilters('savedSet', $event)"
    />
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { mapGetters } from 'vuex';

import { Nullable } from '@/utils/types';
import { PropertyTypeEnum } from '@/bundles/Property/interfaces/PropertyType';
import { IDashboardCardFilters } from '@/bundles/Dashboard/interfaces';
import { AdminStoreDataListEnum } from '@/store/modules/admin/types/AdminStoreDataListEnum';

import SavedSetSelect from '@/bundles/SavedSets/components/SavedSetSelect.vue';

interface IDashboardCardFiltersFieldsData {
  dateStartMenu: boolean;
  dateEndMenu: boolean;
}

export default defineComponent({
  name: 'DashboardCardFiltersFields',

  components: { SavedSetSelect },

  props: {
    modelValue: {
      type: Object as PropType<IDashboardCardFilters>,
      required: true,
    },
    source: {
      type: String,
      required: true,
    },
    column: Boolean,
    dateLabel: {
      type: String,
      default: '',
    },
    defaultStartDate: {
      type: String,
      default: '',
    },
    defaultEndDate: {
      type: String,
      default: '',
    },
    loading: Boolean,
    filterLoading: Boolean,
  },

  emits: ['update:modelValue'],

  data: (): IDashboardCardFiltersFieldsData => ({
    dateStartMenu: false,
    dateEndMenu: false,
  }),

  computed: {
    ...mapGetters('Admin', ['getListByCategory']),
    propertyTypes (): PropertyTypeEnum[] {
      return this.getListByCategory(AdminStoreDataListEnum.property_types).items;
    },
    endDate (): string {
      return this.defaultEndDate || this.modelValue.dateEnd;
    },
    selectStyle (): Nullable<string> {
      return this.column ? null : 'max-width: 200px';
    },
    dateStartModel: {
      get (): Nullable<Date> {
        return this.modelValue.dateStart ? new Date(this.modelValue.dateStart) : null;
      },
      set (date: Nullable<Date>) {
        this.updateDate('dateStart', date ? date.toISOString().slice(0, 10) : '');
      },
    },
    dateEndModel: {
      get (): Nullable<Date> {
        return this.modelValue.dateEnd ? new Date(this.modelValue.dateEnd) : null;
      },
      set (date: Nullable<Date>) {
        this.updateDate('dateEnd', date ? date.toISOString().slice(0, 10) : '');
      },
    },
  },

  methods: {
    updateFilters (field: keyof IDashboardCardFilters, fieldValue: unknown) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: fieldValue });
    },
    updateDate (field: 'dateStart' | 'dateEnd', fieldValue: string) {
      this.dateStartMenu = false;
      this.dateEndMenu = false;
      this.updateFilters(field, fieldValue);
    },
  },
});
</script>
